import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_RESOLVABLE_MISSION_TYPE_DEFAULT } from '../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../contexts/ReferencesContext';

import { STATUS_ID } from './constants/data-grid.constant';
import type { ResolvableMissionTypeListCmp_ResolvableMissionType } from './hooks/useFindAllResolvableMissionTypesQuery.hook';

interface Props {
  readonly resolvableMissionType: ResolvableMissionTypeListCmp_ResolvableMissionType;
}

const Action = ({ resolvableMissionType }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_RESOLVABLE_MISSION_TYPE_DEFAULT, {
      id: resolvableMissionType._id,
    });
    void navigate(path);
  }, [navigate, resolvableMissionType._id]);

  const actionLabel = 'Consulter le Type de Mission';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ResolvableMissionTypeListCmp_ResolvableMissionType>[] =>
  [
    {
      field: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      editable: false,
      sortable: false,
      type: 'actions',
      getActions: (
        params: Readonly<
          GridRowParams<ResolvableMissionTypeListCmp_ResolvableMissionType>
        >,
      ) => [<Action key="consult" resolvableMissionType={params.row} />],
    },
    {
      field: nameof<ResolvableMissionTypeListCmp_ResolvableMissionType>('code'),
      headerName: 'Code',
      width: 350,
      description: 'Code',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field:
        nameof<ResolvableMissionTypeListCmp_ResolvableMissionType>('label'),
      headerName: 'Libellé',
      width: 400,
      description: 'Libellé',
      editable: false,
      sortable: true,
      type: 'string',
    },
    {
      field: STATUS_ID,
      headerName: 'Statut',
      width: 200,
      description: 'Statut',
      editable: false,
      sortable: true,
      hideable: true,
      type: 'singleSelect',
      valueGetter: (
        _: unknown,
        row: ResolvableMissionTypeListCmp_ResolvableMissionType,
      ) =>
        genericStatuses.find(
          (genericStatus) => genericStatus._id === row.statusId,
        )?.code ?? 'Inconnu',
      valueOptions: genericStatuses.map((genericStatus) => ({
        value: genericStatus.code,
        label: genericStatus.label,
      })),
    },
  ] as const;
